import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { ClickAwayListener, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { AviaPassengerType, FlightStatusInfoStatus } from '@websky/graphql';
import { useTheme } from '../../../../theme';
import { useToggleable } from '../../../../hooks';
import Link from '../../../../Link';
import { Land, Takeoff } from '../../../../Icons';
import { format, removeTrailingSlash } from '../../../../utils';
import { endOfDay, parse } from 'date-fns';
import ImageWithFallback from '../../../../ImageWithFallback';
import Amenities from '../../../../Amenities';
import AircraftPopup from '../../../../AircraftPopup';
import { useConfig } from '../../../../context';
import { createFastSearchURL } from '../../../../SearchForm/utils';
import BoardingInfo from '../BoardingInfo/BoardingInfo';
import { CLIENT_FORMAT, SERVER_DATE_FORMAT } from '../../services/constants';
import { BaseButton } from '../../../../index';
const FlightListItem = ({ flight, selectFlight, className, isActive, scrollToItemWhenActive }) => {
    const { FlightListItem: css } = useTheme('FlightStatus');
    const { t } = useTranslation('FlightStatus');
    const { isOpen, close, toggle } = useToggleable(isActive);
    const containerRef = useRef();
    const { FlightStatus: { engineURL } } = useConfig();
    useEffect(() => {
        if (!scrollToItemWhenActive || !isActive || !containerRef.current) {
            return;
        }
        containerRef.current.scrollIntoView({ block: 'center' });
    }, [scrollToItemWhenActive, isActive, containerRef]);
    const fastSearchUrl = `${removeTrailingSlash(engineURL)}#/search/${createFastSearchURL({
        passengers: [
            {
                count: 1,
                passengerType: AviaPassengerType.ADT
            }
        ],
        segments: [
            {
                date: format(endOfDay(new Date()), `yyyy-MM-dd'T'HH:mm:ssXX`),
                departure: {
                    iata: flight.departure.airport.iata,
                    isCity: false
                },
                arrival: {
                    iata: flight.arrival.airport.iata,
                    isCity: false
                }
            }
        ]
    })}`;
    const handleMouseEnter = useCallback(() => {
        selectFlight(flight);
    }, [selectFlight]);
    const renderDurationItem = useCallback((duration, translation) => {
        if (flight.duration[duration] === 0) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            parseInt(flight.duration[duration].toString(10), 10),
            translation,
            "\u00A0"));
    }, [flight]);
    const renderCity = useCallback((type) => {
        return React.createElement("span", { className: css.city }, flight[type].airport.city.name);
    }, [flight]);
    const renderAirportsInfo = useCallback((type) => {
        return (React.createElement("div", { className: cn({
                [css.departure]: type === 'departure',
                [css.arrival]: type === 'arrival'
            }) },
            React.createElement("div", { className: css.airportInfo },
                React.createElement("div", null, type === 'departure' ? Takeoff : Land),
                React.createElement("div", { className: css.time }, flight[type].time),
                React.createElement("div", null, flight[type].airport.iata)),
            React.createElement("div", { className: css.date }, format(parse(flight[type].date, SERVER_DATE_FORMAT, new Date()), CLIENT_FORMAT).replace('.', ','))));
    }, [flight]);
    const handleHeaderClick = useCallback(event => {
        toggle();
        selectFlight(flight);
    }, [isOpen]);
    return (React.createElement(ClickAwayListener, { onClickAway: close },
        React.createElement("div", { ref: containerRef, className: cn(css.wrapper, className, {
                [css.active]: isActive
            }), 
            // onClick={handleClick}
            onMouseEnter: handleMouseEnter },
            React.createElement("div", { className: css.content },
                React.createElement("div", { onClick: handleHeaderClick },
                    React.createElement("div", { className: css.header },
                        React.createElement("span", { className: css.flightNumber },
                            flight.airline.iata,
                            "-",
                            flight.flightNumber),
                        React.createElement("span", { className: cn(css.status, {
                                [css.departed]: flight.status === FlightStatusInfoStatus.Departed,
                                [css.scheduled]: flight.status === FlightStatusInfoStatus.ScheduledDeparture,
                                [css.arrived]: flight.status === FlightStatusInfoStatus.Landed,
                                [css.cancelled]: flight.status === FlightStatusInfoStatus.Canceled,
                                [css.dataExpected]: flight.status === FlightStatusInfoStatus.DataPending
                            }) }, t(flight.status))),
                    React.createElement("div", { className: css.routeInfo },
                        renderCity('departure'),
                        React.createElement("span", { className: css.duration },
                            React.createElement("div", { className: css.durationLine }),
                            React.createElement("div", { className: css.durationTime },
                                renderDurationItem('days', t('translation:d')),
                                renderDurationItem('hours', t('translation:h')),
                                renderDurationItem('minutes', t('translation:m')))),
                        renderCity('arrival')),
                    React.createElement("div", { className: css.airportsInfo },
                        renderAirportsInfo('departure'),
                        renderAirportsInfo('arrival'))),
                React.createElement(Collapse, { in: isActive },
                    React.createElement("div", null,
                        React.createElement(ImageWithFallback, { className: css.aircraftImage, src: flight.aircraft.icon, fallbackSrc: 'https://cdn.websky.aero/content/default-content/images/Aircrafts/738/738.png', alt: flight.aircraft.name }),
                        React.createElement("div", { className: css.aircraftStatistics },
                            React.createElement(AircraftPopup, { renderInBody: true, id: flight.aircraft.name, name: flight.aircraft.name, renderButton: onClick => {
                                    return (React.createElement(Link, { className: css.aircraftName, action: onClick }, flight.aircraft.name));
                                } }),
                            React.createElement(Amenities, { amenities: flight.flightInfo.amenities }))),
                    React.createElement("div", { className: css.controls },
                        React.createElement(BoardingInfo, { renderTrigger: ({ open }) => (React.createElement(Link, { className: css.bookLink, action: open }, t('Boarding info'))) }),
                        React.createElement(BaseButton, { className: css.buyTicket, onClick: () => window.open(fastSearchUrl, '_blank'), variant: "action" }, t('Buy ticket'))))))));
};
export default FlightListItem;
